/**
 * A field's kinds and its "Looks like" check, as Job setup offers them. One copy, read by the
 * setup screen (App.tsx) and by support's setup actions (main/support-setup-actions.ts), so a
 * field set up remotely has exactly the kinds and the shape check a click gives it.
 *
 * No imports: the main-process build and the support tests load this file as it is.
 */

/** Every kind a field can have. `document-type` is the type's own fixed field, never one a person adds. */
export const fieldKinds = [
  'document-type',
  'text',
  'identifier',
  'date',
  'date-time',
  'currency',
  'number',
  'integer',
  'decimal',
  'percentage',
  'phone',
  'email',
  'postal-address',
  'barcode',
  'line-items',
] as const;
export type FieldKind = (typeof fieldKinds)[number];

/** A job's review thresholds before anyone has set them (Job setup › Review), as fractions 0..1. */
export const REVIEW_POLICY_DEFAULTS = { documentType: 0.7, fields: 0.5, boundary: 0.9 };

/**
 * The expected shape a "Looks like" example stands for (the field's `valuePattern`), or null when
 * the example makes no check: fewer than 3 or more than 64 characters, no digit, or nothing but
 * digits. Letters stand for any letters, digits for any digits, a run of spaces for spaces.
 */
export const valuePatternFromExample = (raw: string): string | null => {
  const value = raw.trim();
  if (value.length < 3 || value.length > 64) return null;
  const characters = Array.from(value);
  let pattern = '^';
  let hasLetter = false;
  let hasDigit = false;
  let hasLiteral = false;
  for (let index = 0; index < characters.length;) {
    const current = characters[index];
    if (/\p{L}/u.test(current)) {
      let end = index + 1;
      while (end < characters.length && /\p{L}/u.test(characters[end])) end++;
      const count = end - index;
      pattern += `\\p{L}${count === 1 ? '' : `{${count}}`}`;
      hasLetter = true;
      index = end;
      continue;
    }
    if (/\p{N}/u.test(current)) {
      let end = index + 1;
      while (end < characters.length && /\p{N}/u.test(characters[end])) end++;
      const count = end - index;
      pattern += `\\d${count === 1 ? '' : `{${count}}`}`;
      hasDigit = true;
      index = end;
      continue;
    }
    if (/\s/u.test(current)) {
      while (index < characters.length && /\s/u.test(characters[index])) index++;
      pattern += '\\s+';
      continue;
    }
    pattern += current.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    hasLiteral = true;
    index++;
  }
  return hasDigit && (hasLetter || hasLiteral) ? `${pattern}$` : null;
};
